import { TextField, InputAdornment, Button } from '@mui/material';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCompanyAddress,
  selectDate,
  selectDefultProfileSetting,
  selectDepartmentName,
  selectPrefix,
  selectUserType,
} from '../utility/Redux/profile';
import moment from 'moment';
import { selectToken } from '../utility/Redux/security';
import {
  setAlertWithTitle,
  setSessionExpired,
  setSpinnerLoading,
} from '../utility/Redux/modal';
import { useState } from 'react';
import { FormatNumber2 } from '../utility/component/tools';
import AlertModalTwoButton from '../utility/component/modals/alertWithTwoButton';
import Lottie from 'lottie-react';
import spinner from '../utility/json/Spinner.json';
import { useNavigate } from 'react-router-dom';
import { getMessageWallet, low_Bal_alert } from '../utility/func/common';
import { not_permisson_forWhastapp } from '../utility/common';
import { CallAPIPromise } from '../utility/func/func';
import './home-dashboard.css';

const sumBy = (items, key) =>
  items.reduce((total, item) => total + (Number(item?.[key]) || 0), 0);

const measurementGroupClass = (title = '') =>
  title.toLowerCase().includes('lower') ? 'is-lower' : 'is-upper';

const garmentCardClass = (type = '') => {
  const normalizedType = String(type).toUpperCase();
  if (normalizedType === 'UPPER') return 'garment-card--upper';
  if (normalizedType === 'LOWER') return 'garment-card--lower';
  return '';
};

const makeFullOrderDetail = (orderRows) => {
  const first = orderRows[0];
  const uniqueItems = Array.from(
    new Map(
      orderRows.map((row) => [
        `${row.UPPER_LOWER}-${row.ITEM_NAME}`,
        `${row.ITEM_NAME || row.UPPER_LOWER} (${row.QNTY || 0} pcs)`,
      ])
    ).values()
  );
  const measurements = ['UPPER', 'LOWER']
    .map((type) => ({
      title: `${type === 'UPPER' ? 'Upper' : 'Lower'} measurements`,
      items: Array.from(
        new Map(
          orderRows
            .filter(
              (row) =>
                row.UPPER_LOWER === type &&
                row.PARAMETER &&
                row.SIZE !== undefined &&
                row.SIZE !== null &&
                row.SIZE !== ''
            )
            .map((row) => [
              row.PARAMETER,
              { label: row.PARAMETER, value: row.SIZE },
            ])
        ).values()
      ),
    }))
    .filter((group) => group.items.length > 0);

  return {
    orderNumber: first.BOOKING_ORDER_NO,
    title: first.CUSTOMER_NAME || first.ITEM_NAME || 'Customer order',
    subtitle: `Order #${first.BOOKING_ORDER_NO}`,
    rows: [
      { label: 'Customer', value: first.CUSTOMER_NAME },
      { label: 'Mobile', value: first.MOBILE },
      { label: 'MRC number', value: first.MRC_NO },
      { label: 'Order number', value: first.BOOKING_ORDER_NO },
      { label: 'Items', value: uniqueItems.join(', ') },
      { label: 'City', value: first.CITY },
      { label: 'Address', value: first.ADDRESS },
      {
        label: 'Booking date',
        value: first.DATE ? moment(first.DATE).format('DD MMM YYYY') : '',
      },
      {
        label: 'Trial date',
        value: first.TRY_DATE
          ? moment(first.TRY_DATE).format('DD MMM YYYY')
          : '',
      },
      {
        label: 'Delivery date',
        value: first.DEL_DATE
          ? moment(first.DEL_DATE).format('DD MMM YYYY')
          : '',
      },
      { label: 'Remarks', value: first.REMARKS },
      {
        label: 'Status',
        value: first.DELIVERED_YN === 'YES' ? 'Delivered' : 'Pending',
      },
    ],
    measurements,
  };
};

const DashboardSection = ({
  icon,
  title,
  subtitle,
  count,
  tone = 'neutral',
  action,
  children,
}) => (
  <section className={`dashboard-section dashboard-section--${tone}`}>
    <div className="dashboard-section__header">
      <div className="dashboard-section__title-wrap">
        <span className="dashboard-section__icon" aria-hidden="true">
          <i className={`bi ${icon}`}></i>
        </span>
        <div>
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
      </div>
      <div className="dashboard-section__tools">
        {typeof count === 'number' && (
          <span className="dashboard-section__count">{count}</span>
        )}
        {action}
      </div>
    </div>
    {children}
  </section>
);

const GarmentIcon = ({ type }) => (
  <img
    className="garment-icon"
    src={require(`../utility/images/${type === 'UPPER' ? '2' : '3'}.png`)}
    alt=""
    aria-hidden="true"
  />
);

const DashboardDetailModal = ({ detail, onClose, onOrderClick }) => {
  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  if (!detail) return null;

  return (
    <div className="dashboard-detail-overlay" onMouseDown={onClose}>
      <div
        className={`dashboard-detail-modal ${
          detail.orders?.length ? 'dashboard-detail-modal--order-list' : ''
        } ${detail.selectedOrder ? 'dashboard-detail-modal--expanded' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dashboard-detail-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="dashboard-detail-modal__header">
          <span className="dashboard-detail-modal__icon" aria-hidden="true">
            <i className={`bi ${detail.icon || 'bi-receipt'}`}></i>
          </span>
          <div>
            <small>{detail.eyebrow || 'Details'}</small>
            <h2 id="dashboard-detail-title">{detail.title}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close details">
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
        {detail.subtitle && (
          <p className="dashboard-detail-modal__subtitle">{detail.subtitle}</p>
        )}
        <div className="dashboard-detail-modal__rows">
          {detail.rows
            .filter((row) => row.value !== undefined && row.value !== null && row.value !== '')
            .map((row) => (
              <div key={row.label}>
                <span>{row.label}</span>
                <strong>{row.value}</strong>
              </div>
            ))}
        </div>
        {detail.note && (
          <div className="dashboard-detail-modal__note">
            <i className="bi bi-info-circle"></i>
            <span>{detail.note}</span>
          </div>
        )}
        {!detail.loading && detail.orders?.length > 0 && (
          <div className="dashboard-detail-modal__orders">
            <h3>
              <i className="bi bi-people"></i>
              Customer orders
            </h3>
            <div>
              {detail.orders.map((order, index) => (
                <button
                  type="button"
                  key={`${order.BOOKING_ORDER_NO}-${order.ICODE || index}`}
                  className={
                    detail.selectedOrder?.orderNumber ===
                    order.BOOKING_ORDER_NO
                      ? 'is-selected'
                      : ''
                  }
                  onClick={() => onOrderClick(order)}
                >
                  <span>
                    <strong>{order.CUSTOMER_NAME || 'Customer'}</strong>
                    <small>
                      Order #{order.BOOKING_ORDER_NO || '—'} · {order.ITEM_NAME}
                      {order.QNTY ? ` · ${order.QNTY} pcs` : ''}
                    </small>
                  </span>
                  <span className="dashboard-detail-modal__order-date">
                    {order.DEL_DATE
                      ? moment(order.DEL_DATE).format('DD MMM')
                      : ''}
                    <i className="bi bi-chevron-right"></i>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
        {detail.loading && (
          <div className="dashboard-detail-modal__loading">
            <span></span>
            Loading full order details…
          </div>
        )}
        {!detail.loading && detail.measurements?.length > 0 && (
          <div className="dashboard-detail-modal__measurements">
            {detail.measurements.map((group) => (
              <section
                key={group.title}
                className={measurementGroupClass(group.title)}
              >
                <h3>
                  <i className="bi bi-rulers"></i>
                  {group.title}
                </h3>
                <div>
                  {group.items.map((measurement) => (
                    <span key={measurement.label}>
                      <small>{measurement.label}</small>
                      <strong>{measurement.value}</strong>
                    </span>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
        {detail.loadError && (
          <p className="dashboard-detail-modal__error">{detail.loadError}</p>
        )}
        {detail.selectedOrder && (
          <aside className="dashboard-detail-modal__selected-order">
            <div className="dashboard-detail-modal__selected-header">
              <div>
                <small>Selected customer order</small>
                <h2>{detail.selectedOrder.title}</h2>
                <p>{detail.selectedOrder.subtitle}</p>
              </div>
              <button
                type="button"
                onClick={() => onOrderClick(null)}
                aria-label="Close selected order"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            {detail.selectedOrder.loading ? (
              <div className="dashboard-detail-modal__loading">
                <span></span>
                Loading customer and measurements…
              </div>
            ) : (
              <>
                <div className="dashboard-detail-modal__rows">
                  {detail.selectedOrder.rows
                    .filter(
                      (row) =>
                        row.value !== undefined &&
                        row.value !== null &&
                        row.value !== ''
                    )
                    .map((row) => (
                      <div key={row.label}>
                        <span>{row.label}</span>
                        <strong>{row.value}</strong>
                      </div>
                    ))}
                </div>
                {detail.selectedOrder.measurements?.length > 0 && (
                  <div className="dashboard-detail-modal__measurements">
                    {detail.selectedOrder.measurements.map((group) => (
                      <section
                        key={group.title}
                        className={measurementGroupClass(group.title)}
                      >
                        <h3>
                          <i className="bi bi-rulers"></i>
                          {group.title}
                        </h3>
                        <div>
                          {group.items.map((measurement) => (
                            <span key={measurement.label}>
                              <small>{measurement.label}</small>
                              <strong>{measurement.value}</strong>
                            </span>
                          ))}
                        </div>
                      </section>
                    ))}
                  </div>
                )}
                {detail.selectedOrder.loadError && (
                  <p className="dashboard-detail-modal__error">
                    {detail.selectedOrder.loadError}
                  </p>
                )}
              </>
            )}
          </aside>
        )}
        <div className="dashboard-detail-modal__actions">
          <button
            type="button"
            className="dashboard-detail-modal__done"
            onClick={onClose}
          >
            Close
          </button>
          {detail.primaryAction && (
            <button
              type="button"
              className="dashboard-detail-modal__primary"
              onClick={detail.primaryAction.onClick}
            >
              {detail.primaryAction.label}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const HomeDashboard = () => {
  const prefix = useSelector(selectPrefix);
  const token = useSelector(selectToken);
  const [todaySale, setTodaySale] = useState();
  const [weeklySale, setWeeklySale] = useState([]);
  const [monthlySale, setMonthlySale] = useState([]);
  const [todayBookings, setTodayBookings] = useState([]);
  const [todayExpDelivery, setTodayExpectedDelivery] = useState([]);
  const [todayAdvancePaymentReciept, setTodayAdvancePaymentReciept] = useState(
    []
  );

  const [alertTwoButtonModal, setAlertTwoButtonModal] = useState({
    title: '',
    msg: '',
    status: false,
    setStatus: '',
    button1: '',
    button1Click: '',
    button2: '',
    button2Click: '',
  });
  const companyDetail = useSelector(selectCompanyAddress);
  const [monthlyDelCount, setMonthlyDelCount] = useState([]);
  const [weeklyDelCount, setWeeklyDelCount] = useState([]);
  const [todayCanceledBookings, setTodayCanceledBookings] = useState([]);
  const [todayTry, setTodayExpectedTry] = useState([]);
  const [todayDeliveredItem, setTodayDeliverdItem] = useState([]);
  const [cardDetail, setCardDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const checkCurrentDate = useSelector(selectDate);
  const [mobile, setMobile] = useState('');
  const [firstExpersion, setFirstExp] = useState(true);
  const defultProfile = useSelector(selectDefultProfileSetting);
  const userType = useSelector(selectUserType);
  const getTodaySale = () => {
    setLoading(true);
    CallAPIPromise(`/api/dashBoardRoute/todaySale`, {
      table_prefix: prefix,
      DATE: moment(checkCurrentDate).format('YYYY-MM-DD'),
    })
      .then(function (response) {
        if (!response.data.error) {
          setTodaySale(response.data.response[0]);
        }
      })
      .catch(function (error) {
        console.log(error);
        if (error.response.data.name === 'TokenExpiredError') {
          dispatch(setSessionExpired(true));
        }
      })
      .finally(() => {
        getTodayPaymentReciept();
      });
  };

  const getTodayPaymentReciept = () => {
    CallAPIPromise(`/api/dashBoardRoute/todayPaymentReceipt`, {
      table_prefix: prefix,
      DATE: moment(checkCurrentDate).format('YYYY-MM-DD'),
    })
      .then(function (response) {
        if (!response.data.error) {
          setTodayAdvancePaymentReciept(response.data.response);
        }
      })
      .catch(function (error) {
        console.log(error);
        if (error.response.data.name === 'TokenExpiredError') {
          dispatch(setSessionExpired(true));
        }
      })
      .finally(() => {
        getWeeklySale();
      });
  };

  const getWeeklySale = () => {
    CallAPIPromise(`/api/dashBoardRoute/weeklySale`, {
      table_prefix: prefix,
      FDATE: moment(checkCurrentDate).subtract(7, 'days').format('YYYY-MM-DD'),
      TDATE: moment(checkCurrentDate).format('YYYY-MM-DD'),
    })
      .then(function (response) {
        if (!response.data.error) {
          setWeeklySale(response.data.response);
        }
      })
      .catch(function (error) {
        console.log(error);
        if (error.response.data.name === 'TokenExpiredError') {
          dispatch(setSessionExpired(true));
        }
      })
      .finally(() => {
        getMonthlySale();
      });
  };
  const departmentName = useSelector(selectDepartmentName);
  const sendtryMsgAPI = (
    name,
    mobile,
    tryDate,
    time,
    companyname,
    compnyMobile,
    email,
    orderNumber
  ) => {
    const data = {
      type: 'invoice',
      toPhoneNumber: mobile,
      INVNO: '',
      BOOKING_ORDER_NO: orderNumber,
      AMOUNT: '',
      TRY_DATE: tryDate ? moment(tryDate).format('YYYY-MM-DD') : '',
      DEL_DATE: '',
      prefix: prefix,
      template_for: 'garment_ready_to_delivery',
      companyEmail: email,
      company_name: companyname,
      company_mobile: compnyMobile,
      message_for: 'garment_ready_to_delivery',
      rejected_reason: '',
    };
    dispatch(setSpinnerLoading('Sending Message'));
    CallAPIPromise('/api/whatsApp/send-whatsapp-message', data)
      .then(function (response) {
        dispatch(
          setAlertWithTitle({
            tilte: 'success',
            msg: 'Whatsapp message sent successfully',
            lottie: 'success',
          })
        );
      })
      .catch(function (error) {
        console.log(error);
        if (error.response.data.name === 'TokenExpiredError') {
          dispatch(setSessionExpired(true));
        }
        dispatch(
          setAlertWithTitle({
            tilte: 'Error',
            msg: 'Whatsapp message not sent',
            lottie: 'reject',
          })
        );
      })
      .finally(() => {
        dispatch(setSpinnerLoading(false));
      });
  };

  const sendDelMsgAPI = (
    name,
    mobile,
    delDate,
    time,
    companyname,
    compnyMobile,
    email,
    orderNumber,
    customer
  ) => {
    const data = {
      type: 'garment_ready_to_delivery',
      toPhoneNumber: mobile,
      INVNO: '',
      BOOKING_ORDER_NO: orderNumber,
      AMOUNT: '',
      TRY_DATE: '',
      DEL_DATE: delDate ? moment(delDate).format('YYYY-MM-DD') : '',
      prefix: prefix,
      template_for: 'garment_ready_to_delivery',
      companyEmail: email,
      company_name: companyname,
      CUSTOMER_NAME: customer,
      company_mobile: compnyMobile,
      message_for: 'garment_ready_to_delivery',
      rejected_reason: '',
    };
    dispatch(setSpinnerLoading('Sending Message'));
    CallAPIPromise('/api/whatsApp/send-whatsapp-message', data)
      .then(function (response) {
        dispatch(
          setAlertWithTitle({
            title: 'success',
            msg: 'Whatsapp message sent successfully',
            lottie: 'success',
          })
        );
      })
      .catch(function (error) {
        console.log(error);

        dispatch(
          setAlertWithTitle({
            title: 'Error',
            msg: 'Whatsapp message not sent',
            lottie: 'reject',
          })
        );
      })
      .finally(() => {
        dispatch(setSpinnerLoading(false));
      });
  };
  const getMonthlySale = () => {
    CallAPIPromise(`/api/dashBoardRoute/monthlySale`, {
      table_prefix: prefix,
    })
      .then(function (response) {
        if (!response.data.error) {
          setMonthlySale(response.data.response);
        }
      })
      .catch(function (error) {
        console.log(error);
        if (error.response.data.name === 'TokenExpiredError') {
          dispatch(setSessionExpired(true));
        }
      })
      .finally(() => {
        getTodayBooking();
      });
  };
  const getTodayBooking = (m) => {
    CallAPIPromise(`/api/dashBoardRoute/todayBookings`, {
      table_prefix: prefix,
      DATE: moment(checkCurrentDate).format('YYYY-MM-DD'),
      MOBILE: mobile,
    })
      .then(function (response) {
        if (!response.data.error) {
          setTodayBookings(response.data.response);
        }
      })
      .catch(function (error) {
        console.log(error);
        setTodayBookings([]);
        if (error.response.data.name === 'TokenExpiredError') {
          dispatch(setSessionExpired(true));
        }
      })
      .finally(() => {
        getTodayExpectedDelivery();
      });
  };

  const getTodayExpectedDelivery = () => {
    CallAPIPromise(`/api/dashBoardRoute/todayExpDelivery`, {
      table_prefix: prefix,
      DEL_DATE: moment(checkCurrentDate).format('YYYY-MM-DD'),
      MOBILE: mobile,
    })
      .then(function (response) {
        if (!response.data.error) {
          setTodayExpectedDelivery(response.data.response);
        }
      })
      .catch(function (error) {
        console.log(error);
        if (error.response.data.name === 'TokenExpiredError') {
          dispatch(setSessionExpired(true));
        }
      })
      .finally(() => {
        getWeeklyDelCount();
      });
  };

  const getWeeklyDelCount = () => {
    CallAPIPromise(`/api/dashBoardRoute/weeklyDeliveryCount`, {
      table_prefix: prefix,
      FDATE: moment(checkCurrentDate).format('YYYY-MM-DD'),
      TDATE: moment(checkCurrentDate).add(7, 'days').format('YYYY-MM-DD'),
      MOBILE: mobile,
    })
      .then(function (response) {
        if (!response.data.error) {
          setWeeklyDelCount(response.data.response);
        }
      })
      .catch(function (error) {
        console.log(error);
        if (error.response.data.name === 'TokenExpiredError') {
          dispatch(setSessionExpired(true));
        }
      })
      .finally(() => {
        getMonthlyDelCount();
      });
  };

  const getMonthlyDelCount = () => {
    CallAPIPromise(`/api/dashBoardRoute/monthlyDeliveryCount`, {
      table_prefix: prefix,
      FDEL_DATE: moment(checkCurrentDate).format('YYYY-MM-DD'),
      TDEL_DATE: moment(checkCurrentDate).add(30, 'days').format('YYYY-MM-DD'),
      MOBILE: mobile,
    })
      .then(function (response) {
        if (!response.data.error) {
          setMonthlyDelCount(response.data.response);
        }
      })
      .catch(function (error) {
        console.log(error);
        if (error.response.data.name === 'TokenExpiredError') {
          dispatch(setSessionExpired(true));
        }
      })
      .finally(() => {
        getTodayCanceldBooking();
      });
  };

  const getTodayCanceldBooking = () => {
    CallAPIPromise(`/api/dashBoardRoute/todayCanceledBookings`, {
      table_prefix: prefix,
      DATE: moment(checkCurrentDate).format('YYYY-MM-DD'),
      MOBILE: mobile,
    })
      .then(function (response) {
        if (!response.data.error) {
          setTodayCanceledBookings(response.data.response);
        }
      })
      .catch(function (error) {
        console.log(error);
        if (error.response.data.name === 'TokenExpiredError') {
          dispatch(setSessionExpired(true));
        }
      })
      .finally(() => {
        getTodayExpectedTry();
      });
  };

  const getTodayExpectedTry = () => {
    CallAPIPromise(`/api/dashBoardRoute/todayExpTry`, {
      table_prefix: prefix,
      TRY_DATE: moment(checkCurrentDate).format('YYYY-MM-DD'),
      MOBILE: mobile,
    })
      .then(function (response) {
        if (!response.data.error) {
          setTodayExpectedTry(response.data.response);
        }
      })
      .catch(function (error) {
        console.log(error);
        if (error.response.data.name === 'TokenExpiredError') {
          dispatch(setSessionExpired(true));
        }
      })
      .finally(() => {
        getTodayDeliveredItems();
      });
  };

  const getTodayDeliveredItems = () => {
    CallAPIPromise(`/api/dashBoardRoute/todayDelivered`, {
      table_prefix: prefix,
      F_DELIVERED_DATE: moment(checkCurrentDate).format('YYYY-MM-DD'),
      T_DELIVERED_DATE: moment(checkCurrentDate).format('YYYY-MM-DD'),
      MOBILE: mobile,
    })
      .then(function (response) {
        if (!response.data.error) {
          setTodayDeliverdItem(response.data.response);
        }
      })
      .catch(function (error) {
        console.log(error);
        if (error.response.data.name === 'TokenExpiredError') {
          dispatch(setSessionExpired(true));
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  function setDeliveryStatusAPI(bookingNo, icode, status) {
    CallAPIPromise(`/api/booking/deliverItem`, {
      table_prefix: prefix,
      BOOKING_ORDER_NO: bookingNo,
      ICODE: icode,
      DELIVERED_YN: status,
      DELIVERED_DATE: moment(checkCurrentDate).format('YYYY-MM-DD'),
    })
      .then(function (response) {
        if (!response.data.error) {
          setAlertTwoButtonModal((current) => ({
            ...current,
            status: false,
          }));
          setCardDetail(null);

          CallAPIPromise(`/api/dashBoardRoute/todayExpDelivery`, {
            table_prefix: prefix,
            DEL_DATE: moment(checkCurrentDate).format('YYYY-MM-DD'),
            MOBILE: mobile,
          })
            .then(function (response) {
              if (!response.data.error) {
                setTodayExpectedDelivery(response.data.response);
              }
            })
            .catch(function (error) {
              console.log(error);
              if (error.response.data.name === 'TokenExpiredError') {
                dispatch(setSessionExpired(true));
              }
            });
          getTodayDeliveredItems();
        } else {
          dispatch(
            setAlertWithTitle({
              title: 'Error 126',
              msg: response.data.error,
            })
          );
        }
      })
      .catch(function (error) {
        console.log(error);
        dispatch(
          setAlertWithTitle({
            title: 'Error 125',
            msg: 'Something went wrong while Updating Delivery status',
          })
        );
        if (error.response.data.name === 'TokenExpiredError') {
          dispatch(setSessionExpired(true));
        }
      });
  }

  const confirmDelivery = (item) => {
    setCardDetail(null);
    setAlertTwoButtonModal((current) => ({
      ...current,
      title: `Deliver order #${item.BOOKING_ORDER_NO || '—'}`,
      msg: `Do you want to deliver ${item.ITEM_NAME || 'this item'} for ${
        item.CUSTOMER_NAME || 'this customer'
      }?`,
      status: true,
      button1: 'Yes, deliver',
      button1Click: () =>
        setDeliveryStatusAPI(item.BOOKING_ORDER_NO, item.ICODE, 'YES'),
      button2: 'Cancel',
      button2Click: () =>
        setAlertTwoButtonModal((currentAlert) => ({
          ...currentAlert,
          status: false,
        })),
    }));
  };

  useEffect(() => {
    getTodaySale();
  }, [checkCurrentDate]);

  useEffect(() => {
    if (!mobile && !firstExpersion) {
      getTodayBooking();
    }
  }, [mobile]);

  const totalSale = todaySale
    ? ['CASH_AMOUNT', 'CARD_AMOUNT', 'EPAYMENT', 'ADVANCE_AMT'].reduce(
        (total, key) => total + (Number(todaySale[key]) || 0),
        0
      )
    : 0;
  const bookedCount = sumBy(todayBookings, 'QNTY');
  const deliveryCount = sumBy(todayExpDelivery, 'QNTY');
  const trialCount = sumBy(todayTry, 'QNTY');

  const showOrderDetail = (
    item,
    title = 'Order details',
    primaryAction = null
  ) => {
    const baseDetail = {
      icon: 'bi-receipt-cutoff',
      eyebrow: title,
      title: item.CUSTOMER_NAME || item.ITEM_NAME || 'Order',
      subtitle: item.BOOKING_ORDER_NO
        ? `Order #${item.BOOKING_ORDER_NO}`
        : 'Order information',
      rows: [
        { label: 'Customer', value: item.CUSTOMER_NAME },
        { label: 'Mobile', value: item.MOBILE },
        { label: 'Order number', value: item.BOOKING_ORDER_NO },
        { label: 'Item', value: item.ITEM_NAME },
        { label: 'Quantity', value: item.QNTY },
        { label: 'City', value: item.CITY },
        { label: 'Trial date', value: item.TRY_DATE ? moment(item.TRY_DATE).format('DD MMM YYYY') : '' },
        { label: 'Delivery date', value: item.DEL_DATE ? moment(item.DEL_DATE).format('DD MMM YYYY') : '' },
        { label: 'Status', value: item.DELIVERED_YN === 'YES' ? 'Delivered' : item.DELIVERED_YN === 'NO' ? 'Pending' : '' },
      ],
      primaryAction,
      loading: Boolean(item.BOOKING_ORDER_NO),
    };

    setCardDetail(baseDetail);
    if (!item.BOOKING_ORDER_NO) return;

    CallAPIPromise('/api/booking/loadCustomerOrder', {
      table_prefix: prefix,
      BOOKING_ORDER_NO: item.BOOKING_ORDER_NO,
    })
      .then((response) => {
        if (response.data.error || !response.data.response?.length) {
          throw new Error('Order details were not found.');
        }

        const orderRows = response.data.response;
        const first = orderRows[0];
        const uniqueItems = Array.from(
          new Map(
            orderRows.map((row) => [
              `${row.UPPER_LOWER}-${row.ITEM_NAME}`,
              `${row.ITEM_NAME || row.UPPER_LOWER} (${row.QNTY || 0} pcs)`,
            ])
          ).values()
        );
        const measurementGroups = ['UPPER', 'LOWER']
          .map((type) => {
            const items = Array.from(
              new Map(
                orderRows
                  .filter(
                    (row) =>
                      row.UPPER_LOWER === type &&
                      row.PARAMETER &&
                      row.SIZE !== undefined &&
                      row.SIZE !== null &&
                      row.SIZE !== ''
                  )
                  .map((row) => [
                    row.PARAMETER,
                    { label: row.PARAMETER, value: row.SIZE },
                  ])
              ).values()
            );
            return { title: `${type === 'UPPER' ? 'Upper' : 'Lower'} measurements`, items };
          })
          .filter((group) => group.items.length > 0);

        setCardDetail((current) =>
          current
            ? {
                ...current,
                title: first.CUSTOMER_NAME || current.title,
                subtitle: `Order #${first.BOOKING_ORDER_NO}`,
                loading: false,
                rows: [
                  { label: 'Customer', value: first.CUSTOMER_NAME },
                  { label: 'Mobile', value: first.MOBILE },
                  { label: 'MRC number', value: first.MRC_NO },
                  { label: 'Order number', value: first.BOOKING_ORDER_NO },
                  { label: 'Items', value: uniqueItems.join(', ') },
                  { label: 'City', value: first.CITY },
                  { label: 'Address', value: first.ADDRESS },
                  { label: 'Booking date', value: first.DATE ? moment(first.DATE).format('DD MMM YYYY') : '' },
                  { label: 'Trial date', value: first.TRY_DATE ? moment(first.TRY_DATE).format('DD MMM YYYY') : '' },
                  { label: 'Delivery date', value: first.DEL_DATE ? moment(first.DEL_DATE).format('DD MMM YYYY') : '' },
                  { label: 'Remarks', value: first.REMARKS },
                  { label: 'Status', value: first.DELIVERED_YN === 'YES' ? 'Delivered' : 'Pending' },
                ],
                measurements: measurementGroups,
              }
            : current
        );
      })
      .catch((error) => {
        console.log(error);
        if (error.response?.data?.name === 'TokenExpiredError') {
          dispatch(setSessionExpired(true));
        }
        setCardDetail((current) =>
          current
            ? {
                ...current,
                loading: false,
                loadError: 'Full measurements could not be loaded. Basic order details are shown above.',
              }
            : current
        );
      });
  };

  const showSummaryDetail = ({ icon, eyebrow, title, subtitle, rows }) =>
    setCardDetail({ icon, eyebrow, title, subtitle, rows });

  const showUpcomingOrderDetail = (item) => {
    if (!item) {
      setCardDetail((current) =>
        current ? { ...current, selectedOrder: null } : current
      );
      return;
    }

    setCardDetail((current) =>
      current
        ? {
            ...current,
            selectedOrder: {
              orderNumber: item.BOOKING_ORDER_NO,
              title: item.CUSTOMER_NAME || 'Customer',
              subtitle: `Order #${item.BOOKING_ORDER_NO || '—'}`,
              loading: true,
              rows: [],
            },
          }
        : current
    );

    CallAPIPromise('/api/booking/loadCustomerOrder', {
      table_prefix: prefix,
      BOOKING_ORDER_NO: item.BOOKING_ORDER_NO,
    })
      .then((response) => {
        if (response.data.error || !response.data.response?.length) {
          throw new Error('Order details were not found.');
        }
        const fullDetail = makeFullOrderDetail(response.data.response);
        setCardDetail((current) =>
          current
            ? {
                ...current,
                selectedOrder: { ...fullDetail, loading: false },
              }
            : current
        );
      })
      .catch((error) => {
        console.log(error);
        if (error.response?.data?.name === 'TokenExpiredError') {
          dispatch(setSessionExpired(true));
        }
        setCardDetail((current) =>
          current
            ? {
                ...current,
                selectedOrder: {
                  ...current.selectedOrder,
                  loading: false,
                  rows: [
                    { label: 'Customer', value: item.CUSTOMER_NAME },
                    { label: 'Mobile', value: item.MOBILE },
                    { label: 'Order number', value: item.BOOKING_ORDER_NO },
                    { label: 'Item', value: item.ITEM_NAME },
                    { label: 'Quantity', value: item.QNTY },
                  ],
                  loadError:
                    'Full measurements could not be loaded for this order.',
                },
              }
            : current
        );
      });
  };

  const showUpcomingDeliveryDetail = (item, days, list) => {
    const itemCount = Number(item.NOOF_DELIVERY) || 0;
    const totalCount = sumBy(list, 'NOOF_DELIVERY');
    const fromDate = moment(checkCurrentDate).format('DD MMM YYYY');
    const toDate = moment(checkCurrentDate).add(days, 'days').format('DD MMM YYYY');

    setCardDetail({
      icon: 'bi-calendar-check',
      eyebrow: `Next ${days} days`,
      title: `${item.ITEM_NAME} deliveries`,
      subtitle: `${itemCount} ${item.ITEM_NAME} garments are scheduled for delivery.`,
      rows: [
        { label: 'Garment', value: item.ITEM_NAME },
        { label: 'This garment', value: `${itemCount} deliveries` },
        { label: 'All garments', value: `${totalCount} deliveries` },
        { label: 'From', value: fromDate },
        { label: 'To', value: toDate },
        {
          label: 'Average workload',
          value: `${(totalCount / days).toFixed(1)} deliveries/day`,
        },
      ],
      note:
        'Loading customer-wise orders. Select an order below to see its full customer and measurement details.',
      loading: true,
    });

    const deliveryRequests = Array.from({ length: days + 1 }, (_, index) =>
      CallAPIPromise('/api/dashBoardRoute/todayExpDelivery', {
        table_prefix: prefix,
        DEL_DATE: moment(checkCurrentDate)
          .add(index, 'days')
          .format('YYYY-MM-DD'),
        MOBILE: '',
      })
    );

    Promise.all(deliveryRequests)
      .then((responses) => {
        const uniqueOrders = Array.from(
          new Map(
            responses
              .flatMap((response) => response.data?.response || [])
              .filter(
                (order) =>
                  order.ITEM_NAME === item.ITEM_NAME &&
                  (!item.UPPER_LOWER ||
                    order.UPPER_LOWER === item.UPPER_LOWER) &&
                  (!item.DEL_DATE ||
                    moment(order.DEL_DATE).format('YYYY-MM-DD') ===
                      moment(item.DEL_DATE).format('YYYY-MM-DD'))
              )
              .map((order) => [
                `${order.BOOKING_ORDER_NO}-${order.ICODE}-${order.ITEM_NAME}`,
                order,
              ])
          ).values()
        );

        setCardDetail((current) =>
          current
            ? {
                ...current,
                loading: false,
                orders: uniqueOrders,
                rows: [
                  { label: 'Garment', value: item.ITEM_NAME },
                  { label: 'Matching orders', value: uniqueOrders.length },
                  { label: 'Garments due', value: sumBy(uniqueOrders, 'QNTY') },
                  { label: 'All garments', value: `${totalCount} deliveries` },
                  { label: 'From', value: fromDate },
                  { label: 'To', value: toDate },
                ],
                note: uniqueOrders.length
                  ? 'Select any customer order below to view measurements, dates, remarks and full customer information.'
                  : 'No individual orders were returned for this summary card. The aggregate delivery count is shown above.',
              }
            : current
        );
      })
      .catch((error) => {
        console.log(error);
        if (error.response?.data?.name === 'TokenExpiredError') {
          dispatch(setSessionExpired(true));
        }
        setCardDetail((current) =>
          current
            ? {
                ...current,
                loading: false,
                loadError:
                  'Customer-wise upcoming orders could not be loaded. Please try again.',
              }
            : current
        );
      });
  };

  const sendTrialReminder = (item) => {
    if (defultProfile?.SEND_BILL_BY_WHATSAPP !== 'Yes') {
      dispatch(
        setAlertWithTitle({ title: 'Alert', msg: not_permisson_forWhastapp })
      );
      return;
    }

    setAlertTwoButtonModal({
      ...alertTwoButtonModal,
      title: `Order #${item.BOOKING_ORDER_NO || '—'}`,
      msg: `${item.CUSTOMER_NAME || 'Customer'} • ${item.ITEM_NAME || 'Garment'} (${item.QNTY || 0} pcs) • ${item.MOBILE || 'No mobile number'}. Send trial reminder?`,
      status: true,
      button1: 'Send',
      button1Click: () => {
        setAlertTwoButtonModal((current) => ({ ...current, status: false }));
        getMessageWallet(companyDetail.email, token).then((balance) => {
          if (balance > 0) {
            sendtryMsgAPI(
              item.CUSTOMER_NAME,
              item.MOBILE,
              item.TRY_DATE,
              '03:00 pm',
              companyDetail.companyName,
              companyDetail.mobile,
              companyDetail.email,
              item.BOOKING_ORDER_NO
            );
          } else {
            dispatch(setAlertWithTitle({ title: 'Alert', msg: low_Bal_alert }));
          }
        });
      },
      button2: 'Cancel',
      button2Click: () =>
        setAlertTwoButtonModal((current) => ({ ...current, status: false })),
    });
  };

  const sendAllTrialReminders = () => {
    if (defultProfile?.SEND_BILL_BY_WHATSAPP !== 'Yes') {
      dispatch(
        setAlertWithTitle({ title: 'Alert', msg: not_permisson_forWhastapp })
      );
      return;
    }

    setAlertTwoButtonModal({
      ...alertTwoButtonModal,
      title: 'Message all customers',
      msg: `Send trial reminders to ${todayTry.length} customers?`,
      status: true,
      button1: 'Send all',
      button1Click: () => {
        setAlertTwoButtonModal((current) => ({ ...current, status: false }));
        getMessageWallet(companyDetail.email, token).then((balance) => {
          if (balance >= todayTry.filter((item) => item.MOBILE).length) {
            todayTry.forEach((item) => {
              if (item.MOBILE) sendTrialReminderDirect(item);
            });
          } else {
            dispatch(setAlertWithTitle({ title: 'Alert', msg: low_Bal_alert }));
          }
        });
      },
      button2: 'Cancel',
      button2Click: () =>
        setAlertTwoButtonModal((current) => ({ ...current, status: false })),
    });
  };

  const sendTrialReminderDirect = (item) =>
    sendtryMsgAPI(
      item.CUSTOMER_NAME,
      item.MOBILE,
      item.TRY_DATE,
      '03:00 pm',
      companyDetail.companyName,
      companyDetail.mobile,
      companyDetail.email,
      item.BOOKING_ORDER_NO
    );

  if (process.env.REACT_APP_CLASSIC_DASHBOARD !== 'true') {
    return (
      <main className="home-dashboard">
        <header className="dashboard-welcome">
          <div>
            <span className="dashboard-welcome__eyebrow">Today at a glance</span>
            <h1>Your dashboard</h1>
            <p>Important work and payments, all in one place.</p>
          </div>
          <div className="dashboard-live-pill">
            <span></span>
            Updated for today
          </div>
        </header>

        <section className="dashboard-overview" aria-label="Today's summary">
          {departmentName !== 'Delivery' && (
            <button
              type="button"
              className="overview-card overview-card--money"
              onClick={() =>
                showSummaryDetail({
                  icon: 'bi-currency-rupee',
                  eyebrow: "Today's money",
                  title: FormatNumber2(totalSale),
                  subtitle: 'Total money received today',
                  rows: [
                    { label: 'Cash', value: FormatNumber2(todaySale?.CASH_AMOUNT || 0) },
                    { label: 'Card', value: FormatNumber2(todaySale?.CARD_AMOUNT || 0) },
                    { label: 'Online payment', value: FormatNumber2(todaySale?.EPAYMENT || 0) },
                    { label: 'Advance', value: FormatNumber2(todaySale?.ADVANCE_AMT || 0) },
                  ],
                })
              }
            >
              <span className="overview-card__icon" aria-hidden="true">
                <i className="bi bi-currency-rupee"></i>
              </span>
              <div>
                <p>Money received today</p>
                <strong>{FormatNumber2(totalSale)}</strong>
                <small>Cash, card, online & advance</small>
              </div>
            </button>
          )}
          {departmentName !== 'Delivery' && (
            <button
              type="button"
              className="overview-card overview-card--orders"
              onClick={() =>
                showSummaryDetail({
                  icon: 'bi-bag-check',
                  eyebrow: "Today's orders",
                  title: `${bookedCount} garments`,
                  subtitle: `${todayBookings.length} order entries booked today`,
                  rows: todayBookings.slice(0, 6).map((item, index) => ({
                    label: item.ITEM_NAME || `Order ${index + 1}`,
                    value: `${item.QNTY || 0} pcs${item.CUSTOMER_NAME ? ` • ${item.CUSTOMER_NAME}` : ''}`,
                  })),
                })
              }
            >
              <span className="overview-card__icon" aria-hidden="true">
                <i className="bi bi-bag-check"></i>
              </span>
              <div>
                <p>New orders today</p>
                <strong>{bookedCount}</strong>
                <small>Garments booked</small>
              </div>
            </button>
          )}
          <button
            type="button"
            className="overview-card overview-card--delivery"
            onClick={() =>
              showSummaryDetail({
                icon: 'bi-box-seam',
                eyebrow: "Today's deliveries",
                title: `${deliveryCount} garments`,
                subtitle: `${todayExpDelivery.length} customer orders due today`,
                rows: todayExpDelivery.slice(0, 6).map((item, index) => ({
                  label: item.CUSTOMER_NAME || `Order ${index + 1}`,
                  value: `#${item.BOOKING_ORDER_NO || '—'} • ${item.ITEM_NAME || 'Garment'} • ${item.QNTY || 0} pcs`,
                })),
              })
            }
          >
            <span className="overview-card__icon" aria-hidden="true">
              <i className="bi bi-box-seam"></i>
            </span>
            <div>
              <p>Deliver today</p>
              <strong>{deliveryCount}</strong>
              <small>Garments due</small>
            </div>
          </button>
          {departmentName !== 'Delivery' && (
            <button
              type="button"
              className="overview-card overview-card--trial"
              onClick={() =>
                showSummaryDetail({
                  icon: 'bi-person-check',
                  eyebrow: "Today's trials",
                  title: `${trialCount} garments`,
                  subtitle: `${todayTry.length} customers have trials today`,
                  rows: todayTry.slice(0, 6).map((item, index) => ({
                    label: item.CUSTOMER_NAME || `Customer ${index + 1}`,
                    value: `#${item.BOOKING_ORDER_NO || '—'} • ${item.ITEM_NAME || 'Garment'}`,
                  })),
                })
              }
            >
              <span className="overview-card__icon" aria-hidden="true">
                <i className="bi bi-person-check"></i>
              </span>
              <div>
                <p>Trials today</p>
                <strong>{trialCount}</strong>
                <small>Customers to contact</small>
              </div>
            </button>
          )}
        </section>

        <section className="dashboard-search" aria-label="Find a customer">
          <div className="dashboard-search__copy">
            <span className="dashboard-search__icon" aria-hidden="true">
              <i className="bi bi-search"></i>
            </span>
            <div>
              <strong>Find customer</strong>
              <small>Enter their mobile number</small>
            </div>
          </div>
          <div className="dashboard-search__form">
            <label className="sr-only" htmlFor="dashboard-mobile-search">
              Customer mobile number
            </label>
            <input
              id="dashboard-mobile-search"
              inputMode="numeric"
              autoComplete="tel"
              placeholder="Mobile number"
              value={mobile}
              onChange={(event) => {
                setFirstExp(false);
                setMobile(event.target.value.replace(/\D/g, ''));
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') getTodayBooking();
              }}
            />
            <button type="button" onClick={() => getTodayBooking()}>
              <i className="bi bi-search" aria-hidden="true"></i>
              Search
            </button>
            {departmentName === 'Delivery' && (
              <button
                type="button"
                className="dashboard-search__scan"
                onClick={() => navigate('/Scan_Qr_Code')}
              >
                <i className="bi bi-qr-code-scan" aria-hidden="true"></i>
                Scan QR
              </button>
            )}
          </div>
        </section>

        {todayExpDelivery.length > 0 && (
          <DashboardSection
            icon="bi-box-seam"
            title="Deliver today"
            subtitle="Tap a customer to review the order and mark it delivered."
            count={todayExpDelivery.length}
            tone="delivery"
          >
            <div className="work-card-grid">
              {todayExpDelivery.map((item, index) => (
                <button
                  className={`work-card ${garmentCardClass(
                    item.UPPER_LOWER
                  )}`}
                  type="button"
                  key={`${item.BOOKING_ORDER_NO}-${item.ICODE || index}`}
                  onClick={() =>
                    showOrderDetail(item, 'Delivery due', {
                      label: 'Deliver item',
                      onClick: () => confirmDelivery(item),
                    })
                  }
                >
                  <div className="work-card__top">
                    <span className="work-card__garment">
                      <GarmentIcon type={item.UPPER_LOWER} />
                    </span>
                    <span
                      className={`status-pill ${
                        item.DELIVERED_YN === 'YES'
                          ? 'status-pill--done'
                          : 'status-pill--pending'
                      }`}
                    >
                      <i
                        className={`bi ${
                          item.DELIVERED_YN === 'YES'
                            ? 'bi-check-circle-fill'
                            : 'bi-clock-fill'
                        }`}
                      ></i>
                      {item.DELIVERED_YN === 'YES' ? 'Delivered' : 'Waiting'}
                    </span>
                  </div>
                  <strong>{item.CUSTOMER_NAME || 'Customer'}</strong>
                  <span className="work-card__order">
                    <i className="bi bi-receipt"></i>
                    Order #{item.BOOKING_ORDER_NO || '—'}
                  </span>
                  <span className="work-card__item">
                    {item.ITEM_NAME} · {item.QNTY} pcs
                  </span>
                  <span className="work-card__phone">
                    <i className="bi bi-telephone"></i>
                    {item.MOBILE || 'No mobile number'}
                  </span>
                  {item.CITY && (
                    <span className="work-card__phone">
                      <i className="bi bi-geo-alt"></i>
                      {item.CITY}
                    </span>
                  )}
                  <span className="work-card__action">
                    View & deliver
                    <i className="bi bi-arrow-right"></i>
                  </span>
                </button>
              ))}
            </div>
          </DashboardSection>
        )}

        {todayTry.length > 0 && departmentName !== 'Delivery' && (
          <DashboardSection
            icon="bi-person-check"
            title="Trials today"
            subtitle="Customers who should visit for a fitting."
            count={todayTry.length}
            tone="trial"
            action={
              <button
                type="button"
                className="section-action-button"
                onClick={sendAllTrialReminders}
              >
                <i className="bi bi-whatsapp"></i>
                Message all
              </button>
            }
          >
            <div className="work-card-grid">
              {todayTry.map((item, index) => (
                <button
                  className="work-card"
                  type="button"
                  key={`${item.BOOKING_ORDER_NO}-${item.ICODE || index}`}
                  onClick={() =>
                    showOrderDetail(item, 'Trial due', {
                      label: 'Send reminder',
                      onClick: () => {
                        setCardDetail(null);
                        sendTrialReminder(item);
                      },
                    })
                  }
                >
                  <div className="work-card__top">
                    <span className="work-card__garment">
                      <GarmentIcon type={item.UPPER_LOWER} />
                    </span>
                    <span className="status-pill status-pill--trial">
                      <i className="bi bi-scissors"></i>
                      Trial
                    </span>
                  </div>
                  <strong>{item.CUSTOMER_NAME || 'Customer'}</strong>
                  <span className="work-card__item">
                    {item.ITEM_NAME} · {item.QNTY} pcs
                  </span>
                  <span className="work-card__phone">
                    <i className="bi bi-telephone"></i>
                    {item.MOBILE || 'No mobile number'}
                  </span>
                  <span className="work-card__action">
                    Send reminder <i className="bi bi-arrow-right"></i>
                  </span>
                </button>
              ))}
            </div>
          </DashboardSection>
        )}

        {todayBookings.length > 0 && departmentName !== 'Delivery' && (
          <DashboardSection
            icon="bi-bag-check"
            title="New orders today"
            subtitle="Garments booked today."
            count={todayBookings.length}
            tone="orders"
          >
            <div className="compact-card-grid">
              {todayBookings.map((item, index) => (
                <button
                  type="button"
                  className={`compact-card compact-card--button ${garmentCardClass(
                    item.UPPER_LOWER
                  )}`}
                  key={`${item.ITEM_NAME}-${index}`}
                  onClick={() => showOrderDetail(item, 'New order')}
                >
                  <span className="compact-card__icon">
                    <GarmentIcon type={item.UPPER_LOWER} />
                  </span>
                  <div>
                    <strong>{item.ITEM_NAME}</strong>
                    <span>{item.QNTY} garments</span>
                  </div>
                  <i className="bi bi-chevron-right compact-card__chevron"></i>
                </button>
              ))}
            </div>
          </DashboardSection>
        )}

        {todayAdvancePaymentReciept.length > 0 &&
          departmentName !== 'Delivery' && (
            <DashboardSection
              icon="bi-wallet2"
              title="Payments received today"
              subtitle="Advance and receipt payments by method."
              tone="money"
            >
              <div className="payment-grid">
                {todayAdvancePaymentReciept.map((item, index) => (
                  <button
                    type="button"
                    className="payment-card"
                    key={`${item.PAY_MODE}-${index}`}
                    onClick={() =>
                      showSummaryDetail({
                        icon: 'bi-wallet2',
                        eyebrow: "Today's payment",
                        title: item.PAY_MODE || 'Payment',
                        subtitle: 'Amount received today',
                        rows: [
                          { label: 'Payment method', value: item.PAY_MODE || 'Payment' },
                          { label: 'Amount', value: FormatNumber2(item.CREDIT || 0) },
                        ],
                      })
                    }
                  >
                    <span>{item.PAY_MODE || 'Payment'}</span>
                    <strong>{FormatNumber2(item.CREDIT)}</strong>
                  </button>
                ))}
              </div>
            </DashboardSection>
          )}

        {departmentName !== 'Delivery' &&
          (weeklySale.length > 0 || monthlySale.length > 0) && (
            <details className="dashboard-details" open>
              <summary>
                <span>
                  <i className="bi bi-graph-up-arrow"></i>
                  Sales history
                </span>
                <small>Weekly and monthly totals</small>
              </summary>
              <div className="history-columns">
                {weeklySale.length > 0 && (
                  <div>
                    <h3>Last 7 days</h3>
                    <div className="history-list">
                      {weeklySale.map((item, index) => (
                        <button
                          type="button"
                          key={`${item.DAY_NAME}-${index}`}
                          onClick={() =>
                            showSummaryDetail({
                              icon: 'bi-graph-up-arrow',
                              eyebrow: 'Daily sale',
                              title: item.DAY_NAME,
                              subtitle: 'Payment breakup for this day',
                              rows: [
                                { label: 'Cash', value: FormatNumber2(item.CASH_AMOUNT || 0) },
                                { label: 'Card', value: FormatNumber2(item.CARD_AMOUNT || 0) },
                                { label: 'Online payment', value: FormatNumber2(item.EPAYMENT || 0) },
                              ],
                            })
                          }
                        >
                          <span>{item.DAY_NAME}</span>
                          <strong>
                            {FormatNumber2(
                              (Number(item.CASH_AMOUNT) || 0) +
                                (Number(item.CARD_AMOUNT) || 0) +
                                (Number(item.EPAYMENT) || 0)
                            )}
                          </strong>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {monthlySale.length > 0 && (
                  <div>
                    <h3>By month</h3>
                    <div className="history-list">
                      {monthlySale.map((item, index) => (
                        <button
                          type="button"
                          key={`${item.MONTH_NAME}-${index}`}
                          onClick={() =>
                            showSummaryDetail({
                              icon: 'bi-calendar3',
                              eyebrow: 'Monthly sale',
                              title: item.MONTH_NAME,
                              subtitle: 'Payment breakup for this month',
                              rows: [
                                { label: 'Cash', value: FormatNumber2(item.CASH_AMOUNT || 0) },
                                { label: 'Card', value: FormatNumber2(item.CARD_AMOUNT || 0) },
                                { label: 'Online payment', value: FormatNumber2(item.EPAYMENT || 0) },
                              ],
                            })
                          }
                        >
                          <span>{item.MONTH_NAME}</span>
                          <strong>
                            {FormatNumber2(
                              (Number(item.CASH_AMOUNT) || 0) +
                                (Number(item.CARD_AMOUNT) || 0) +
                                (Number(item.EPAYMENT) || 0)
                            )}
                          </strong>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </details>
          )}

        {departmentName !== 'Delivery' &&
          (weeklyDelCount.length > 0 || monthlyDelCount.length > 0) && (
            <details className="dashboard-details" open>
              <summary>
                <span>
                  <i className="bi bi-calendar-check"></i>
                  Upcoming deliveries
                </span>
                <small>Next 7 and 30 days</small>
              </summary>
              <div className="history-columns">
                {weeklyDelCount.length > 0 && (
                  <div>
                    <h3>Next 7 days</h3>
                    <div className="delivery-count-grid">
                      {weeklyDelCount.map((item, index) => (
                        <button
                          type="button"
                          className={garmentCardClass(item.UPPER_LOWER)}
                          key={`${item.ITEM_NAME}-${index}`}
                          onClick={() =>
                            showUpcomingDeliveryDetail(item, 7, weeklyDelCount)
                          }
                        >
                          <GarmentIcon type={item.UPPER_LOWER} />
                          <span>{item.ITEM_NAME}</span>
                          <strong>{item.NOOF_DELIVERY}</strong>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {monthlyDelCount.length > 0 && (
                  <div>
                    <h3>Next 30 days</h3>
                    <div className="delivery-count-grid">
                      {monthlyDelCount.map((item, index) => (
                        <button
                          type="button"
                          className={garmentCardClass(item.UPPER_LOWER)}
                          key={`${item.ITEM_NAME}-${index}`}
                          onClick={() =>
                            showUpcomingDeliveryDetail(item, 30, monthlyDelCount)
                          }
                        >
                          <GarmentIcon type={item.UPPER_LOWER} />
                          <span>{item.ITEM_NAME}</span>
                          <strong>{item.NOOF_DELIVERY}</strong>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </details>
          )}

        {todayDeliveredItem.length > 0 && departmentName !== 'Delivery' && (
          <DashboardSection
            icon="bi-check2-circle"
            title="Delivered today"
            subtitle="Completed deliveries. Use undo only when a status is wrong."
            count={todayDeliveredItem.length}
            tone="complete"
          >
            <div className="compact-card-grid">
              {todayDeliveredItem.map((item, index) => (
                <button
                  type="button"
                  className="compact-card compact-card--button"
                  key={`${item.BOOKING_ORDER_NO}-${item.ICODE || index}`}
                  onClick={() =>
                    showOrderDetail(item, 'Delivered order', {
                      label: 'Undo delivery',
                      onClick: () => {
                        setCardDetail(null);
                        setAlertTwoButtonModal({
                          ...alertTwoButtonModal,
                          title: `Order #${item.BOOKING_ORDER_NO || '—'}`,
                          msg: `${item.CUSTOMER_NAME || 'Customer'} • ${item.ITEM_NAME || 'Garment'} (${item.QNTY || 0} pcs). Mark as not delivered?`,
                          status: true,
                          button1: 'Yes, undo',
                          button1Click: () =>
                            setDeliveryStatusAPI(
                              item.BOOKING_ORDER_NO,
                              item.ICODE,
                              'NO'
                            ),
                          button2: 'Keep delivered',
                          button2Click: () =>
                            setAlertTwoButtonModal((current) => ({
                              ...current,
                              status: false,
                            })),
                        });
                      },
                    })
                  }
                >
                  <span className="compact-card__icon compact-card__icon--done">
                    <i className="bi bi-check-lg"></i>
                  </span>
                  <div>
                    <strong>{item.CUSTOMER_NAME || item.ITEM_NAME}</strong>
                    <span>
                      {item.ITEM_NAME} · {item.QNTY} pcs
                    </span>
                  </div>
                  <span className="compact-card__undo">Undo</span>
                </button>
              ))}
            </div>
          </DashboardSection>
        )}

        {todayCanceledBookings.length > 0 && departmentName !== 'Delivery' && (
          <DashboardSection
            icon="bi-x-circle"
            title="Cancelled today"
            subtitle="Orders cancelled today."
            count={todayCanceledBookings.length}
            tone="cancelled"
          >
            <div className="compact-card-grid">
              {todayCanceledBookings.map((item, index) => (
                <button
                  type="button"
                  className="compact-card compact-card--button"
                  key={`${item.ITEM_NAME}-${index}`}
                  onClick={() => showOrderDetail(item, 'Cancelled order')}
                >
                  <span className="compact-card__icon compact-card__icon--cancelled">
                    <i className="bi bi-x-lg"></i>
                  </span>
                  <div>
                    <strong>{item.ITEM_NAME}</strong>
                    <span>{item.QNTY} garments</span>
                  </div>
                  <i className="bi bi-chevron-right compact-card__chevron"></i>
                </button>
              ))}
            </div>
          </DashboardSection>
        )}

        {loading && (
          <div className="dashboard-loading" role="status">
            <Lottie animationData={spinner} loop={true} />
            <span>Updating dashboard…</span>
          </div>
        )}

        {!loading &&
          !todayExpDelivery.length &&
          !todayTry.length &&
          !todayBookings.length && (
            <div className="dashboard-empty">
              <span><i className="bi bi-check2-circle"></i></span>
              <h2>No urgent work for today</h2>
              <p>New orders, trials and deliveries will appear here.</p>
            </div>
          )}

        <DashboardDetailModal
          detail={cardDetail}
          onClose={() => setCardDetail(null)}
          onOrderClick={showUpcomingOrderDetail}
        />

        <AlertModalTwoButton
          details={alertTwoButtonModal}
          setAlertTwoButtonModal={setAlertTwoButtonModal}
        />
      </main>
    );
  }

  return (
    <div className="h-full w-full text-md  overflow-y-scroll p-3  ">
      {todaySale && departmentName !== 'Delivery' ? (
        <div className="w-full">
          <div
            style={{
              border: '1px solid #b5b3b3',
              fontSize: '13px  ',
              fontWeight: '500',
            }}
            className="w-fit flex px-3 py-1  text-white   rounded-br-3xl bg-[#634258]"
          >
            Today Sale :{' '}
            <div
              style={{
                fontWeight: '400',
              }}
              className="pl-2"
            >
              {FormatNumber2(
                todaySale.CASH_AMOUNT +
                  todaySale.CARD_AMOUNT +
                  todaySale.EPAYMENT +
                  todaySale.ADVANCE_AMT
              )}
            </div>
          </div>
          <div className="grid max-sm:grid-cols-2 tab:grid-cols-3 t:grid-cols-4 d:grid-cols-6 gap-2 mt-2">
            {todaySale ? (
              <>
                {todaySale.CASH_AMOUNT !== 0 ? (
                  <Button
                    variant={'contained'}
                    style={{
                      textTransform: 'none',

                      backgroundColor: 'transparent',
                      padding: '0px',
                      marginBottom: '8px',
                      boxShadow: 'none',
                    }}
                  >
                    <div
                      style={{
                        boxShadow: '1px 1px 1px gray',
                        backgroundColor: '#e8e2db',
                      }}
                      className="flex justify-between w-full p-[0px] h-full rounded-sm"
                    >
                      <div className="p-1 px-2 text-[#0d0c0c]">
                        <div className="text-start ">Cash</div>
                        <div className="text-[#615b5b]">
                          {todaySale.CASH_AMOUNT !== 0
                            ? FormatNumber2(todaySale.CASH_AMOUNT)
                            : ' '}
                        </div>
                      </div>
                    </div>
                  </Button>
                ) : null}

                {todaySale.CARD_AMOUNT !== 0 ? (
                  <Button
                    variant={'contained'}
                    style={{
                      textTransform: 'none',
                      backgroundColor: 'transparent',
                      padding: '0px',
                      marginBottom: '8px',
                      boxShadow: 'none',
                    }}
                  >
                    <div
                      style={{
                        boxShadow: '1px 1px 1px gray',
                        backgroundColor: '#e8e2db',
                      }}
                      className="flex justify-between h-full w-full p-[0px] rounded-sm"
                    >
                      <div className="p-1 px-2 text-[#0d0c0c]">
                        <div className="text-start ">Card</div>
                        <div className="text-[#615b5b]">
                          {' '}
                          {todaySale.CARD_AMOUNT !== 0
                            ? FormatNumber2(todaySale.CARD_AMOUNT)
                            : ' '}
                        </div>
                      </div>
                    </div>
                  </Button>
                ) : null}

                {todaySale.EPAYMENT !== 0 ? (
                  <Button
                    variant={'contained'}
                    style={{
                      textTransform: 'none',
                      backgroundColor: 'transparent',
                      padding: '0px',
                      marginBottom: '8px',
                      boxShadow: 'none',
                    }}
                  >
                    <div
                      style={{
                        boxShadow: '1px 1px 1px gray',
                        backgroundColor: '#e8e2db',
                      }}
                      className="flex justify-between w-full h-full p-[0px] rounded-sm"
                    >
                      <div className="p-1 px-2 text-[#0d0c0c]">
                        <div className="text-start ">ePayment</div>
                        <div className="text-[#615b5b] flex justify-start">
                          {todaySale.EPAYMENT !== 0
                            ? FormatNumber2(todaySale.EPAYMENT)
                            : ' '}
                        </div>
                      </div>
                    </div>
                  </Button>
                ) : null}

                {todaySale.ADVANCE_AMT !== 0 ? (
                  <Button
                    variant={'contained'}
                    style={{
                      textTransform: 'none',
                      backgroundColor: 'transparent',
                      padding: '0px',
                      marginBottom: '8px',
                      boxShadow: 'none',
                    }}
                  >
                    <div
                      style={{
                        boxShadow: '1px 1px 1px gray',
                        backgroundColor: '#e8e2db',
                      }}
                      className="flex justify-between w-full h-full p-[0px] rounded-sm"
                    >
                      <div className="p-1 px-2 text-[#0d0c0c]">
                        <div className="text-start ">Advance</div>
                        <div className="text-[#615b5b] flex justify-start">
                          {todaySale.ADVANCE_AMT !== 0
                            ? FormatNumber2(todaySale.ADVANCE_AMT)
                            : null}
                        </div>
                      </div>
                    </div>
                  </Button>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Today Advance */}
      {todayAdvancePaymentReciept[0] && departmentName !== 'Delivery' ? (
        <div className="w-full mt-4">
          <div
            style={{
              border: '1px solid #b5b3b3',
              fontSize: '13px  ',
              fontWeight: '500',
            }}
            className="w-fit whitespace-nowrap flex  px-3 py-1 text-black   rounded-br-3xl bg-[#f5f5f5]"
          >
            Today Advance & Receipts :{'  '}
            <div
              style={{
                fontWeight: '400',
              }}
              className="pl-2"
            >
              {FormatNumber2(todayAdvancePaymentReciept[0].CREDIT)}
            </div>
          </div>
          <div className="grid max-sm:grid-cols-2 tab:grid-cols-3 t:grid-cols-4 d:grid-cols-6 gap-2 mt-2">
            {todayAdvancePaymentReciept[0]
              ? todayAdvancePaymentReciept.map((e, index) => {
                  return (
                    <Button
                      key={index}
                      variant={'contained'}
                      style={{
                        textTransform: 'none',
                        backgroundColor: 'transparent',
                        padding: '0px',
                        marginBottom: '8px',
                        boxShadow: 'none',
                      }}
                    >
                      <div
                        style={{
                          boxShadow: '1px 1px 1px gray',
                          backgroundColor: '#e8e2db',
                        }}
                        className="flex justify-between w-full p-[0px] rounded-sm"
                      >
                        <div className="bg-[#e8e2db] pb-1  rounded-sm">
                          <div className="text-[16px] p-1 px-2 text-[#0d0c0c] text-start ">
                            {e.PAY_MODE}
                          </div>
                          <div className="px-2 text-[#615b5b]">
                            {FormatNumber2(e.CREDIT)}
                          </div>
                        </div>
                      </div>
                    </Button>
                  );
                })
              : null}
          </div>
        </div>
      ) : null}

      {weeklySale[0] && departmentName !== 'Delivery' ? (
        <div className="w-full mt-3">
          <div
            style={{
              border: '1px solid #b5b3b3',
              fontSize: '13px  ',
              fontWeight: '500',
            }}
            className="w-fit flex px-3 py-1  text-black   rounded-br-3xl bg-[#f5f5f5]"
          >
            Weekly Sale{' '}
          </div>
          <div className="grid max-sm:grid-cols-2 tab:grid-cols-3 t:grid-cols-4 d:grid-cols-6 gap-2 mt-2">
            {weeklySale.map((w, index) => (
              <Button
                key={index}
                variant={'contained'}
                style={{
                  textTransform: 'none',

                  backgroundColor: 'transparent',
                  padding: '0px',
                  marginBottom: '8px',
                  boxShadow: 'none',
                }}
              >
                <div
                  style={{
                    boxShadow: '1px 1px 1px gray',
                    backgroundColor: '#e8e2db',
                  }}
                  className="flex justify-between w-full p-[0px] h-full rounded-sm"
                >
                  <div className="p-1 px-2 text-[#0d0c0c]">
                    <div className="text-start ">{w.DAY_NAME}</div>
                    <div className="text-[#615b5b]">
                      <div className="flex ">
                        Total :{'  '}
                        {FormatNumber2(
                          w.CASH_AMOUNT + w.CARD_AMOUNT + w.EPAYMENT
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Button>
            ))}
          </div>
        </div>
      ) : null}

      {monthlySale[0] && departmentName !== 'Delivery' ? (
        <div className="w-full mt-3">
          <div
            style={{
              border: '1px solid #b5b3b3',
              fontSize: '13px  ',
              fontWeight: '500',
            }}
            className="w-fit flex  px-3 py-1  text-black   rounded-br-3xl bg-[#f5f5f5]"
          >
            Monthly Sale{' '}
          </div>
          <div className="grid max-sm:grid-cols-2 tab:grid-cols-3 t:grid-cols-4 d:grid-cols-6 gap-2 mt-2">
            {monthlySale.map((w, index) => (
              <Button
                key={index}
                variant={'contained'}
                style={{
                  textTransform: 'none',

                  backgroundColor: 'transparent',
                  padding: '0px',
                  marginBottom: '8px',
                  boxShadow: 'none',
                }}
              >
                <div
                  style={{
                    boxShadow: '1px 1px 1px gray',
                    backgroundColor: '#e8e2db',
                  }}
                  className="flex justify-between w-full p-[0px] h-full rounded-sm"
                >
                  <div className="p-1 px-2 text-[#0d0c0c]">
                    <div className="text-start ">{w.MONTH_NAME}</div>
                    <div className="text-[#615b5b]">
                      <div className="flex ">
                        Total :{' '}
                        {FormatNumber2(
                          w.CASH_AMOUNT + w.CARD_AMOUNT + w.EPAYMENT
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Button>
            ))}
          </div>
        </div>
      ) : null}
      <div className="mt-3 flex items-center">
        <TextField
          size="small"
          InputProps={{
            sx: {
              fontSize: '14px',
              backgroundColor: 'white',
              '& .MuiOutlinedInput-notchedOutline': {
                border: '0.5px solid black',
              },
              padding: '0px',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#03a9f4',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: '0.1px solid #03a9f4',
              },
            },
            endAdornment: (
              <InputAdornment
                sx={{
                  padding: '0px',
                }}
                position="end"
              >
                <button
                  onClick={() => {
                    getTodayBooking();
                  }}
                  className="h-[37px] rounded-r-sm px-[12px] bg-red  "
                >
                  <i className="bi text-white bi-search flex items-center"></i>
                </button>
              </InputAdornment>
            ),
          }}
          InputLabelProps={{
            sx: {
              fontSize: '14px',
            },
          }}
          type="mobile"
          name="email"
          id="email"
          label="Search By Mobile"
          onChange={(e) => {
            setFirstExp(false);
            setMobile(e.target.value);
          }}
          value={mobile}
        />

        <div className="h-full flex items-center ml-2">
          {/* <button
            onClick={() => {
              getTodayBooking();
            }}
            className="bg-green p-2 shadow-md rounded-sm px-3"
          >
            Search
          </button> */}

          {departmentName === 'Delivery' && (
            <button
              onClick={() => {
                navigate('/Scan_Qr_Code');
              }}
              className="bg-sky-300  p-2  shadow-md rounded-sm px-3"
            >
              <i className="bi bi-qr-code-scan"></i>
            </button>
          )}
        </div>
      </div>

      {/* Today Booking */}
      {todayBookings[0] && departmentName !== 'Delivery' ? (
        <div className="w-full mt-3">
          <div
            style={{
              border: '1px solid #b5b3b3',
              fontSize: '13px  ',
              fontWeight: '500',
            }}
            className="w-fit px-3 py-1 text-black   rounded-br-3xl bg-[#f8c469]"
          >
            Today Bookings
          </div>
          <div className="grid max-sm:grid-cols-2 tab:grid-cols-3 t:grid-cols-4 d:grid-cols-6 gap-2 mt-2">
            {todayBookings[0]
              ? todayBookings.map((e, index) => {
                  return (
                    <Button
                      key={index}
                      variant={'contained'}
                      style={{
                        textTransform: 'none',
                        backgroundColor: 'transparent',
                        padding: '0px',
                        marginBottom: '8px',
                        boxShadow: 'none',
                      }}
                    >
                      <div
                        style={{
                          boxShadow: '1px 1px 1px gray',
                          backgroundColor:
                            e.UPPER_LOWER === 'UPPER' ? '#e8e2db' : '#f8e4dd',
                        }}
                        className="flex justify-between w-full text-sm pl-1 pb-2 pt-2 rounded-sm"
                      >
                        <div className=" p-2 px-2 w-full  flex  justify-between rounded-sm">
                          <div className="text-[#0d0c0c] text-start">
                            {e.ITEM_NAME + '-' + ' ' + e.QNTY}
                          </div>
                          <div className="text-[16px]  ">
                            <img
                              src={require(`../utility/images/${
                                e.UPPER_LOWER === 'UPPER' ? '2' : '3'
                              }.png`)}
                              style={{
                                height: '25px',
                                width: '25px',
                              }}
                            />{' '}
                          </div>
                        </div>
                      </div>
                    </Button>
                  );
                })
              : null}
          </div>
        </div>
      ) : null}

      {/* Today Delieveries */}
      {todayExpDelivery[0] ? (
        <div className="w-full mt-4">
          <div className="flex items-center  w-fit">
            <div
              style={{
                border: '1px solid #b5b3b3',
                fontSize: '13px  ',
                fontWeight: '500',
              }}
              className="w-fit  px-3 py-1 text-black   rounded-br-3xl bg-[#f1f4c6]"
            >
              Today Expected Deliveries
            </div>{' '}
          </div>
          <div className="grid max-sm:grid-cols-2 tab:grid-cols-3 t:grid-cols-4 d:grid-cols-6 gap-2 mt-2">
            {todayExpDelivery.map((e, index) => {
              return (
                <Button
                  key={index}
                  variant={'contained'}
                  style={{
                    textTransform: 'none',
                    backgroundColor: 'transparent',
                    padding: '0px',
                    marginBottom: '8px',
                    boxShadow: 'none',
                  }}
                  onClick={() => {
                    if (userType === 'PRODUCTION') {
                      navigate('/billing', {
                        state: {
                          customer: {
                            mobile: e.MOBILE,
                            customer_name: e.CUSTOMER_NAME,
                            mrc: e.MRC_NO,
                            prefix: e.PREFIX,
                          },
                          orderID: e.BOOKING_ORDER_NO,
                        },
                      });
                    } else {
                      if (defultProfile?.SEND_BILL_BY_WHATSAPP === 'Yes') {
                        setAlertTwoButtonModal({
                          ...alertTwoButtonModal,
                          title: 'Send Whatsapp Message ',
                          msg: 'Do you want to send whatsapp message ?',
                          status: true,
                          button1: 'YES',
                          button1Click: () => {
                            setAlertTwoButtonModal({
                              ...alertTwoButtonModal,

                              status: false,
                            });
                            getMessageWallet(companyDetail.email, token).then(
                              (bal) => {
                                if (bal > 0) {
                                  sendDelMsgAPI(
                                    e.CUSTOMER_NAME,
                                    e.MOBILE,
                                    moment().format('YYYY-MM-DD'),
                                    '03:00 pm',
                                    companyDetail.companyName,
                                    companyDetail.mobile,
                                    companyDetail.email,
                                    e.BOOKING_ORDER_NO,
                                    e.CUSTOMER_NAME
                                  );
                                } else {
                                  dispatch(
                                    setAlertWithTitle({
                                      title: 'Alert',
                                      msg: low_Bal_alert,
                                    })
                                  );
                                }
                              }
                            );
                          },
                          button2: 'No',
                          button2Click: () => {
                            setAlertTwoButtonModal({
                              ...alertTwoButtonModal,
                              status: false,
                            });
                          },
                        });
                      } else {
                        dispatch(
                          setAlertWithTitle({
                            title: 'Alert',
                            msg: not_permisson_forWhastapp,
                          })
                        );
                      }
                    }
                  }}
                >
                  <div
                    style={{
                      boxShadow: '1px 1px 1px gray',
                      backgroundColor:
                        e.UPPER_LOWER === 'UPPER' ? '#e8e2db' : '#f8e4dd',
                    }}
                    className="flex h-full justify-between w-full p-[0px] rounded-sm"
                  >
                    <div className="rounded-sm  w-full p-1 px-2">
                      <div className="   flex  justify-between w-full ">
                        <div className="font-semibold text-[#0d0c0c] text-start">
                          {e.ITEM_NAME + '-' + ' ' + e.QNTY}
                        </div>
                        <div>
                          <img
                            src={require(`../utility/images/${
                              e.UPPER_LOWER === 'UPPER' ? '2' : '3'
                            }.png`)}
                            style={{
                              height: '25px',
                              width: '25px',
                            }}
                          />
                        </div>
                      </div>

                      <div className="text-sm text-start text-[#615b5b]">
                        {e.CUSTOMER_NAME}
                      </div>
                      <div className="text-start text-[#615b5b]">
                        {e.MOBILE}
                      </div>
                      <div className="flex justify-between ">
                        <div className="text-[#615b5b]">{e.CITY}</div>
                        <div
                          className={`w-[18px] h-[18px] ${
                            e.DELIVERED_YN === 'YES' ? 'bg-green' : 'bg-red'
                          } flex justify-center items-center  rounded-sm`}
                        >
                          {e.DELIVERED_YN === 'YES' ? (
                            <i className="bi bi-check2 justify-center flex items-center text-white"></i>
                          ) : (
                            <i className="bi bi-x justify-center flex items-center text-white"></i>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Button>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Today Try */}
      {todayTry[0] && departmentName !== 'Delivery' ? (
        <div className="w-full mt-4">
          <div className="flex items-center bg-[#f1f4c6] w-fit">
            <div
              style={{
                borderRight: '1px solid #b5b3b3',
                zIndex: 100,
              }}
              className="flex pr-1  rounded-br-3xl bg-white"
            >
              <div
                style={{
                  border: '1px solid #b5b3b3',
                  fontSize: '13px  ',
                  fontWeight: '500',
                }}
                className="w-fit  px-3 py-1 text-black   rounded-br-3xl bg-[#f1f4c6]"
              >
                Today Try
              </div>{' '}
            </div>
            <button
              style={{
                borderRight: '1px solid #b5b3b3',
                borderBottom: '1px solid #b5b3b3',
                zIndex: 50,
                boxShadow: '0px 0px 1px #b5b3b3',
                borderTop: '1px solid #b5b3b3',
              }}
              onClick={() => {
                if (defultProfile.SEND_BILL_BY_WHATSAPP === 'Yes') {
                  setAlertTwoButtonModal({
                    ...alertTwoButtonModal,
                    title: 'Send Whatsapp Message ',
                    msg: 'Do you want to send whatsapp message ?',
                    status: true,
                    button1: 'YES',
                    button1Click: () => {
                      setAlertTwoButtonModal({
                        ...alertTwoButtonModal,

                        status: false,
                      });
                      getMessageWallet(companyDetail.email, token).then(
                        (bal) => {
                          if (bal > todayTry.length - 1) {
                            todayTry.map((e) => {
                              if (e.MOBILE) {
                                sendtryMsgAPI(
                                  e.CUSTOMER_NAME,
                                  e.MOBILE,
                                  e.TRY_DATE,
                                  '03:00 pm',
                                  companyDetail.companyName,
                                  companyDetail.mobile,
                                  companyDetail.email,
                                  e.BOOKING_ORDER_NO
                                );
                              }
                            });
                          } else {
                            dispatch(
                              setAlertWithTitle({
                                title: 'Alert',
                                msg: low_Bal_alert,
                              })
                            );
                          }
                        }
                      );
                    },
                    button2: 'No',
                    button2Click: () => {
                      setAlertTwoButtonModal({
                        ...alertTwoButtonModal,
                        status: false,
                      });
                    },
                  });
                } else {
                  dispatch(
                    setAlertWithTitle({
                      title: 'Alert',
                      msg: not_permisson_forWhastapp,
                    })
                  );
                }
              }}
              className=" text-[13px] border border-l-0 -ml-4 pl-4 "
            >
              <div
                style={{
                  fontSize: '13px  ',
                  fontWeight: '500',
                }}
                className="w-fit  px-3 py-1 text-black    bg-[#f1f4c6]"
              >
                {' '}
                Send Whatsapp to all
              </div>
            </button>
          </div>
          <div className="grid max-sm:grid-cols-1 tab:grid-cols-3 t:grid-cols-4 d:grid-cols-6 gap-2 mt-2">
            {todayTry.map((e, index) => {
              return (
                <Button
                  onClick={() => {
                    if (defultProfile.SEND_BILL_BY_WHATSAPP === 'Yes') {
                      setAlertTwoButtonModal({
                        ...alertTwoButtonModal,
                        title: 'Send Whatsapp Message',
                        msg: 'Do you want to send whatsapp message ?',
                        status: true,
                        button1: 'YES',
                        button1Click: () => {
                          setAlertTwoButtonModal({
                            ...alertTwoButtonModal,

                            status: false,
                          });
                          getMessageWallet(companyDetail.email, token).then(
                            (bal) => {
                              if (bal > 0) {
                                sendtryMsgAPI(
                                  e.CUSTOMER_NAME,
                                  e.MOBILE,
                                  e.TRY_DATE,
                                  '03:00 pm',
                                  companyDetail.companyName,
                                  companyDetail.mobile,
                                  companyDetail.email
                                );
                              } else {
                                dispatch(
                                  setAlertWithTitle({
                                    title: 'Alert',
                                    msg: low_Bal_alert,
                                  })
                                );
                              }
                            }
                          );
                        },
                        button2: 'No',
                        button2Click: () => {
                          setAlertTwoButtonModal({
                            ...alertTwoButtonModal,
                            status: false,
                          });
                        },
                      });
                    } else {
                      dispatch(
                        setAlertWithTitle({
                          title: 'Alert',
                          msg: not_permisson_forWhastapp,
                        })
                      );
                    }
                  }}
                  key={index}
                  variant={'contained'}
                  style={{
                    textTransform: 'none',
                    backgroundColor: 'transparent',
                    padding: '0px',
                    marginBottom: '8px',
                    boxShadow: 'none',
                  }}
                >
                  <div
                    style={{
                      boxShadow: '1px 1px 1px gray',
                      backgroundColor:
                        e.UPPER_LOWER === 'UPPER' ? '#e8e2db' : '#f8e4dd',
                    }}
                    className="flex justify-between h-full w-full p-[0px] rounded-sm"
                  >
                    <div className="rounded-sm  w-full p-1 px-2">
                      <div className="   flex  justify-between w-full ">
                        <div className="font-semibold text-[#0d0c0c] text-start">
                          {e.ITEM_NAME + '-' + ' ' + e.QNTY}{' '}
                        </div>
                        <div>
                          <img
                            src={require(`../utility/images/${
                              e.UPPER_LOWER === 'UPPER' ? '2' : '3'
                            }.png`)}
                            style={{
                              height: '25px',
                              width: '25px',
                            }}
                          />{' '}
                        </div>
                      </div>

                      <div className="text-sm text-start text-[#615b5b]">
                        {e.CUSTOMER_NAME}
                      </div>
                      <div className="text-start text-[#615b5b]">
                        {e.MOBILE}
                      </div>
                      <div className="flex justify-between ">
                        <div className="text-[#615b5b]"> {e.CITY}</div>
                        <div
                          className={`w-[18px] h-[18px] ${
                            e.DELIVERED_YN === 'YES' ? 'bg-green' : 'bg-red'
                          } flex justify-center items-center  rounded-sm`}
                        >
                          {e.DELIVERED_YN === 'YES' ? (
                            <i className="bi bi-check2 justify-center flex items-center text-white"></i>
                          ) : (
                            <i className="bi bi-x justify-center flex items-center text-white"></i>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Button>
              );
            })}
          </div>
        </div>
      ) : null}

      {todayCanceledBookings[0] && departmentName !== 'Delivery' ? (
        <div className="w-full mt-4">
          <div
            style={{
              border: '1px solid #b5b3b3',
              fontSize: '13px  ',
              fontWeight: '500',
            }}
            className="w-fit  px-3 py-1 text-black   rounded-br-3xl bg-[#f5f5f5]"
          >
            Today canceled Booking
          </div>
          <div className="grid max-sm:grid-cols-2 tab:grid-cols-3 t:grid-cols-4 d:grid-cols-6 gap-2 mt-2">
            {todayCanceledBookings.map((e, index) => {
              return (
                <Button
                  key={index}
                  variant={'contained'}
                  style={{
                    textTransform: 'none',
                    backgroundColor: 'transparent',
                    padding: '0px',
                    marginBottom: '8px',
                    boxShadow: 'none',
                  }}
                >
                  <div
                    style={{
                      boxShadow: '1px 1px 1px gray',
                      backgroundColor:
                        e.UPPER_LOWER === 'UPPER' ? '#e8e2db' : '#f8e4dd',
                    }}
                    className="flex  h-full justify-between w-full p-[0px] rounded-sm"
                  >
                    <div className="rounded-sm  w-full p-1 px-2">
                      <div className="   flex  justify-between w-full ">
                        <div className="font-semibold text-[#0d0c0c ] text-start">
                          {e.ITEM_NAME + '-' + ' ' + e.QNTY}{' '}
                        </div>
                        <div>
                          <img
                            src={require(`../utility/images/${
                              e.UPPER_LOWER === 'UPPER' ? '2' : '3'
                            }.png`)}
                            style={{
                              height: '25px',
                              width: '25px',
                            }}
                          />{' '}
                        </div>
                      </div>
                    </div>
                  </div>
                </Button>
              );
            })}
          </div>
        </div>
      ) : null}

      {weeklyDelCount[0] && departmentName !== 'Delivery' ? (
        <div className="w-full mt-4 ">
          <div
            style={{
              border: '1px solid #b5b3b3',
              fontSize: '13px  ',
              fontWeight: '500',
            }}
            className="w-fit  px-3 py-1 text-black   rounded-br-3xl bg-[#f5f5f5]"
          >
            Weekly Delivery Count
          </div>
          <div className=" pr-2 mt-2 ">
            <div className="grid max-sm:grid-cols-2 tab:grid-cols-3 t:grid-cols-4 d:grid-cols-6 gap-2 mt-2">
              {weeklyDelCount.map((e, index) => {
                return (
                  <Button
                    key={index}
                    variant={'contained'}
                    style={{
                      textTransform: 'none',
                      backgroundColor: 'transparent',
                      padding: '0px',
                      marginBottom: '8px',
                      boxShadow: 'none',
                    }}
                  >
                    <div
                      style={{
                        boxShadow: '1px 1px 1px gray',
                        backgroundColor:
                          e.UPPER_LOWER === 'UPPER' ? '#e8e2db' : '#f8e4dd',
                      }}
                      className="flex h-full justify-between w-full pl-1 pt-2 pr-2 pb-2 rounded-sm"
                    >
                      <div className="rounded-sm  w-full p-1 px-2">
                        <div className="   flex  justify-between w-full ">
                          <div className="font-semibold text-black text-start">
                            {e.ITEM_NAME + '-' + ' ' + e.NOOF_DELIVERY}{' '}
                          </div>
                          <div>
                            <img
                              src={require(`../utility/images/${
                                e.UPPER_LOWER === 'UPPER' ? '2' : '3'
                              }.png`)}
                              style={{
                                height: '25px',
                                width: '25px',
                              }}
                            />{' '}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}
      {loading && !monthlyDelCount[0] ? (
        <div className="w-6 h-6">
          <Lottie
            animationData={spinner}
            loop={true}
            style={{ fontSize: 5 }}
            color="gray"
          />
        </div>
      ) : monthlyDelCount[0] && departmentName !== 'Delivery' ? (
        <div className="w-full mt-4 ">
          <div
            style={{
              border: '1px solid #b5b3b3',
              fontSize: '13px  ',
              fontWeight: '500',
            }}
            className="w-fit  px-3 py-1 text-black   rounded-br-3xl bg-[#f5f5f5]"
          >
            Monthly Delivery Count
          </div>
          <div className=" pr-2 mt-2 ">
            <div className="grid max-sm:grid-cols-2 tab:grid-cols-3 t:grid-cols-4 d:grid-cols-6 gap-2 mt-2 ">
              {monthlyDelCount.map((e, index) => {
                return (
                  <Button
                    key={index}
                    variant={'contained'}
                    style={{
                      textTransform: 'none',
                      backgroundColor: 'transparent',
                      padding: '0px',
                      marginBottom: '8px',
                      boxShadow: 'none',
                    }}
                  >
                    <div
                      style={{
                        boxShadow: '1px 1px 1px gray',
                        backgroundColor:
                          e.UPPER_LOWER === 'UPPER' ? '#e8e2db' : '#f8e4dd',
                      }}
                      className="flex justify-between h-full w-full pl-1 pr-2 pt-2 pb-2 rounded-sm"
                    >
                      <div className="rounded-sm  w-full p-1 px-2">
                        <div className="   flex  justify-between w-full ">
                          <div className="font-semibold text-black text-start">
                            {e.ITEM_NAME + '-' + ' ' + e.NOOF_DELIVERY}{' '}
                          </div>
                          <div>
                            <img
                              src={require(`../utility/images/${
                                e.UPPER_LOWER === 'UPPER' ? '2' : '3'
                              }.png`)}
                              style={{
                                height: '25px',
                                width: '25px',
                              }}
                            />{' '}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}

      {todayDeliveredItem[0] && departmentName !== 'Delivery' ? (
        <div className="w-full mt-4 ">
          <div
            style={{
              border: '1px solid #b5b3b3',
              fontSize: '13px  ',
              fontWeight: '500',
            }}
            className="w-fit px-3 py-1 text-black   rounded-br-3xl bg-[#f5f5f5]"
          >
            Today Delivered Item
          </div>
          <div className=" pr-2 mt-2 ">
            <div className="grid max-sm:grid-cols-2 tab:grid-cols-3 t:grid-cols-4 d:grid-cols-6 gap-2 mt-2">
              {todayDeliveredItem.map((e, index) => {
                return (
                  <Button
                    key={index}
                    onDoubleClick={() => {
                      setAlertTwoButtonModal({
                        ...alertTwoButtonModal,
                        title: 'Delivery Status',
                        msg: 'Do you want to cancel Delivery ?',
                        status: true,
                        button1: 'YES',
                        button1Click: () => {
                          setDeliveryStatusAPI(
                            e.BOOKING_ORDER_NO,
                            e.ICODE,
                            'NO'
                          );
                        },
                        button2: 'NO',
                        button2Click: '',
                      });
                    }}
                    variant={'contained'}
                    style={{
                      textTransform: 'none',
                      backgroundColor: 'transparent',
                      padding: '0px',
                      marginBottom: '8px',
                      boxShadow: 'none',
                    }}
                  >
                    <div
                      style={{
                        boxShadow: '1px 1px 1px gray',
                        backgroundColor:
                          e.UPPER_LOWER === 'UPPER' ? '#e8e2db' : '#f8e4dd',
                      }}
                      className="flex h-full justify-between w-full p-[0px] rounded-sm"
                    >
                      <div className="rounded-sm  w-full p-1 px-2">
                        <div className="   flex  justify-between w-full ">
                          <div className="font-semibold text-black text-start">
                            {e.ITEM_NAME + '-' + ' ' + e.QNTY}{' '}
                          </div>
                          <div>
                            <img
                              src={require(`../utility/images/${
                                e.UPPER_LOWER === 'UPPER' ? '2' : '3'
                              }.png`)}
                              style={{
                                height: '25px',
                                width: '25px',
                              }}
                            />{' '}
                          </div>
                        </div>

                        <div className="text-sm text-start text-[#615b5b]">
                          {e.CUSTOMER_NAME}
                        </div>
                        <div className="text-start text-[#615b5b]">
                          {e.MOBILE}
                        </div>
                        <div className="flex justify-between ">
                          <div className="text-[#615b5b]"> {e.CITY}</div>
                          <div
                            className={`w-[18px] h-[18px] ${
                              e.DELIVERED_YN === 'YES' ? 'bg-green' : 'bg-red'
                            } flex justify-center items-center  rounded-sm`}
                          >
                            {e.DELIVERED_YN === 'YES' ? (
                              <i className="bi bi-check2 justify-center flex items-center text-white"></i>
                            ) : (
                              <i className="bi bi-x justify-center flex items-center text-white"></i>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}

      <AlertModalTwoButton
        details={alertTwoButtonModal}
        setAlertTwoButtonModal={setAlertTwoButtonModal}
      />
    </div>
  );
};
